/**
 * Module configuration class.
 *
 * Copyright © Ircam Amplify 2025
 */

import { TierType } from '../../utils/AccountUtil'
import { DownloadUtil } from '../../utils/DownloadUtil'
import { IampLogger, LogTag } from '../../utils/IampLogger'
import { Util } from '../../utils/Util'
import { InputCondition } from './ModuleDef'
import { ModuleInfo } from './ModuleInfo'
import { ModuleInputConfig } from './ModuleInputConfig'
import { ModuleParam } from './ModuleParam'

const SAFARI_DOWNLOAD_DELAY_IN_MS = 500

/**
 * The type of input multiplicity of the job.
 */
export enum MultiInputMode {
  /// The module creates one unique job.
  OneSingleJob = 1,

  /// The module can create several job: one job per input file.

  OneJobPerInputFile,

  /// The module can create one job processing several input files
  /// at the same time.
  OneJobMultipleInput,
}

/**
 * Module class.
 */
export class ModuleConfig {
  info: ModuleInfo
  api_doc_url?: string
  multiplicity?: MultiInputMode

  authorized_tiers?: TierType

  /**
   * The list of inputs this module need to start a processing.
   */
  inputList: ModuleInputConfig[]

  paramList?: ModuleParam<any>[]

  /**
   * The minimum number of parameters needed to be set.
   */
  minParamCount?: number

  output?: { name: string; title: string }[]
  paramsTip?: string

  constructor({
    info,
    inputList,
    paramList,
    api_doc_url,
    multiplicity,
    authorized_tiers,
    minParamCount,
    output,
    paramsTip,
  }: {
    info: ModuleInfo
    inputList: ModuleInputConfig[]
    paramList?: ModuleParam<any>[]
    api_doc_url?: string
    multiplicity?: MultiInputMode
    authorized_tiers?: TierType
    minParamCount?: number
    output?: { name: string; title: string }[]
    paramsTip?: string
  }) {
    this.info = info
    this.api_doc_url = api_doc_url
    this.multiplicity = multiplicity
    this.authorized_tiers = authorized_tiers
    this.inputList = inputList
    this.paramList = paramList
    this.minParamCount = minParamCount
    this.output = output
    this.paramsTip = paramsTip
  }

  /**
   * Returns the minimum number of inputs required by this module.
   */
  getMinimumInputCount(): number {
    // The `reduce` method computes the accumulation of value for each iteration.
    return this.inputList.reduce(
      (sum: any, currentInput: ModuleInputConfig) => {
        let increment = 0
        // Optional inputs are not considered part of minimum number of inputs
        if (currentInput.condition !== InputCondition.Optional) {
          increment = currentInput?.minMultipleInputCount ?? 1
        }
        return sum + increment
      },
      0
    )
  }

  /**
   * Default static method to download the results of task.
   *
   * @param result
   *    The response of a processing request.
   *    Its JSON structure must contain a `report` element.
   */
  static async defaultDownloadResult(result: any) {
    const report = result.report
    if (report.archiveFile?.ias && report.archiveFile?.id) {
      // Archive/Zip file available.
      // This file is supposed to contain all the ias files to download.
      // The user just need to download this archive, instead of all the
      // ias files.
      IampLogger.debug(LogTag.TaskList, 'Download zip or archive file')
      await DownloadUtil.downloadFileWithCognito({
        iasId: report.archiveFile.id,
      })
    } else {
      const fileList = result.fileList

      if (!fileList) {
        throw Error("Can't find file to download.")
      }

      if (fileList && fileList.length > 0) {
        for (const filePath of fileList) {
          const fileName = filePath?.split('/')?.pop() as string

          IampLogger.debug(LogTag.TaskList, 'Download file', fileName)
          DownloadUtil.downloadJSON({ fileName, report })
          if (Util.isSafari()) {
            // Safari forbids multiple download at the same time
            // for one user interaction.
            // Workaround: add a delay between two downloads.
            await Util.sleep(SAFARI_DOWNLOAD_DELAY_IN_MS)
          }
        }
      } else if (fileList.length === 0 && report.resultList) {
        for (const elt of report.resultList) {
          DownloadUtil.downloadJSON({
            fileName: elt.inputFilename,
            report: elt,
          })
          if (Util.isSafari()) {
            // Safari forbids multiple download at the same time
            // for one user interaction.
            // Workaround: add a delay between two downloads.
            await Util.sleep(SAFARI_DOWNLOAD_DELAY_IN_MS)
          }
        }
      } else {
        DownloadUtil.downloadJSON({ fileName: 'report', report: report })
      }
    }
  }

  /**
   * Base implementation of the download of the results.
   *
   * In case of the reception of a custom API response at the end of module's
   * processing, this method is the one to override.
   *
   * @param result
   *    The response of a processing request.
   */
  async downloadResult(result: any) {
    await ModuleConfig.defaultDownloadResult(result)
  }

  /**
   * Download a given list of IAS elements.
   * @param iasList
   *    The list of IAS elements.
   *    Each element must contain at least the fields `ias` and `id`.
   */
  async downloadIasList(iasList: any[]) {
    if (!iasList) {
      throw new Error("Can't find files to download.")
    }

    await Promise.all(
      iasList.map(async (ias: any, index: number) => {
        const iasId = ias?.id
        if (iasId) {
          if (Util.isSafari()) {
            // Safari forbids multiple download at the same time
            // for one user interaction.
            // Workaround: add a delay between two downloads.
            await Util.sleep(SAFARI_DOWNLOAD_DELAY_IN_MS * index)
          }
          return DownloadUtil.downloadFileWithCognito({ iasId: iasId })
        } else {
          return Promise.resolve()
        }
      })
    )
  }
}

export class ModuleConfigAiMusicDetector extends ModuleConfig {
  async downloadResult(result: any) {
    const csvReport = result?.report?.csvReport
    const csvIasId = csvReport?.id

    if (csvIasId) {
      await DownloadUtil.downloadFileWithCognito({ iasId: csvIasId })
    } else {
      throw new Error("Can't find CSV file to download.")
    }
  }
}

export class ModuleConfigLoopExtractor extends ModuleConfig {
  async downloadResult(result: any) {
    await this.downloadIasList(result?.report?.loops)
  }
}

export class ModuleConfigAiReEditor extends ModuleConfig {
  async downloadResult(result: any) {
    await this.downloadIasList(result?.report?.versions)
  }
}

export class ModuleConfigSpeechToText extends ModuleConfig {
  async downloadResult(result: any) {
    const csvIas = result?.report?.csv
    const srtIas = result?.report?.srt
    await this.downloadIasList([csvIas, srtIas])
  }
}

export class ModuleConfigStemSeparator extends ModuleConfig {
  async downloadResult(result: any) {
    const outputEntries = Object.entries(result?.report)
      .filter(
        ([key, value]) =>
          key.startsWith('output') &&
          value &&
          typeof value === 'object' &&
          'ias' in value &&
          'id' in value
      )
      .map(([, value]) => value)

    IampLogger.debug(LogTag.TaskList, 'outputEntries:', outputEntries)
    await this.downloadIasList(outputEntries)
  }
}

export class ModuleConfigEditBox extends ModuleConfig {
  async downloadResult(result: any) {
    if (result?.report?.outputFilePart1 && result?.report?.outputFilePart2) {
      await this.downloadIasList([
        result?.report?.outputFilePart1,
        result?.report?.outputFilePart2,
      ])
    } else if (result?.report?.outputFile) {
      await this.downloadIasList([result?.report?.outputFile])
    }
  }
}

export class ModuleConfigTimePitchControl extends ModuleConfig {
  async downloadResult(result: any) {
    if (result?.report?.outputFile) {
      await this.downloadIasList([result?.report?.outputFile])
    }
  }
}

export class ModuleConfigTalkAnalyzer extends ModuleConfig {
  async downloadResult(result: any) {
    if (result?.report?.outputFile) {
      await this.downloadIasList([result?.report?.outputFile])
    }
  }
}

export class ModuleConfigAudioTextAligner extends ModuleConfig {
  async downloadResult(result: any) {
    if (result?.report?.outputSrtFile) {
      await this.downloadIasList([result?.report?.outputSrtFile])
    }
  }
}
